import { NavLink, Outlet } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Globe,
  Inbox,
  Key,
  LayoutDashboard,
  LogOut,
  Mail,
  Rocket,
  Send,
  Server,
  ShieldBan,
  ShieldCheck,
  Users,
  Webhook,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/quickstart", label: "Send API", icon: Rocket },
  { to: "/messages", label: "Messages", icon: Mail },
  { to: "/batches", label: "Batches", icon: BarChart3 },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/domains", label: "Domains", icon: Globe },
  { to: "/api-keys", label: "API Keys", icon: Key },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/validator", label: "Validator", icon: ShieldCheck },
  { to: "/suppressions", label: "Suppressions", icon: ShieldBan },
  { to: "/ips", label: "Dedicated IPs", icon: Server },
  { to: "/inbound/routes", label: "Inbound Routes", icon: Inbox },
  { to: "/inbound/messages", label: "Inbound Log", icon: Mail },
  { to: "/team", label: "Team", icon: Users },
  { to: "/sub-accounts", label: "Sub-accounts", icon: Building2 },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/audit", label: "Audit log", icon: Activity },
];

const OPS_NAV = [
  { to: "/ops/ips", label: "Ops · IP Mgmt", icon: Server },
];

export function AppLayout() {
  const { logout, me, account } = useAuth();

  const accountInitials = (account?.name || account?.slug || me?.accountId || "?")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <div className="min-h-screen flex bg-[#F7F8FB]">
      {/* Sidebar with gradient + geometric mood */}
      <aside className="w-64 shrink-0 border-r border-mailtera-border flex flex-col relative overflow-hidden bg-gradient-to-b from-white via-[#F5F8FD] to-[#EDF2FA]">
        {/* Layer 1: Radial gradient glows di corners */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background: `
              radial-gradient(ellipse 380px 260px at 100% 0%, rgba(68, 114, 196, 0.16) 0%, transparent 60%),
              radial-gradient(ellipse 320px 240px at 0% 100%, rgba(26, 58, 110, 0.12) 0%, transparent 60%),
              radial-gradient(ellipse 220px 180px at 100% 100%, rgba(59, 130, 246, 0.08) 0%, transparent 60%)
            `,
          }}
        />
        {/* Layer 2: Geometric SVG pattern — repeat tile 72×72 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='72' height='72' viewBox='0 0 72 72'>
                <g fill='none' stroke='%231A3A6E' stroke-width='1' opacity='0.6'>
                  <circle cx='12' cy='12' r='2'/>
                  <circle cx='36' cy='36' r='2'/>
                  <circle cx='60' cy='60' r='2'/>
                  <path d='M6 60 L18 48'/>
                  <path d='M54 12 L66 24'/>
                  <path d='M30 6 L42 6'/>
                  <path d='M30 66 L42 66'/>
                  <polygon points='36,20 40,28 32,28' fill='%234472C4' fill-opacity='0.35'/>
                  <polygon points='58,44 62,52 54,52' fill='%234472C4' fill-opacity='0.35'/>
                </g>
              </svg>`,
            )}")`,
            backgroundRepeat: "repeat",
          }}
        />
        {/* Layer 3: Subtle floating accent circle top-right */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(68, 114, 196, 0.5) 0%, transparent 70%)",
          }}
        />

        {/* Logo header */}
        <div className="relative h-16 px-5 flex items-center overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              background:
                "linear-gradient(135deg, rgba(26, 58, 110, 0.08) 0%, transparent 60%)",
            }}
          />
          <div className="relative flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-brand-500 flex items-center justify-center shadow-md shadow-brand-600/30"
            >
              <Send size={17} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-semibold text-[14px] tracking-tight text-brand-600 leading-none">
                Mailtera Send
              </div>
              <div className="text-[9px] uppercase tracking-[0.15em] text-slate-400 font-bold mt-1">
                Dashboard
              </div>
            </div>
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(68, 114, 196, 0.35) 50%, transparent 100%)",
            }}
          />
        </div>

        {/* Nav */}
        <nav className="relative flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition group ${
                    isActive
                      ? "bg-white/80 backdrop-blur-sm text-brand-700 font-medium shadow-sm border border-brand-100"
                      : "text-slate-700 hover:bg-white/60 hover:backdrop-blur-sm"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-gradient-to-b from-brand-500 to-brand-600"
                      />
                    )}
                    <Icon
                      size={16}
                      className={
                        isActive
                          ? "text-brand-600"
                          : "text-slate-500 group-hover:text-slate-700"
                      }
                    />
                    {item.label}
                  </>
                )}
              </NavLink>
            );
          })}
          {me?.isPlatformAdmin && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase font-semibold tracking-wider text-slate-400">
                Platform Ops
              </div>
              {OPS_NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition group ${
                        isActive
                          ? "bg-white/80 backdrop-blur-sm text-brand-700 font-medium shadow-sm border border-brand-100"
                          : "text-slate-700 hover:bg-white/60 hover:backdrop-blur-sm"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-gradient-to-b from-brand-500 to-brand-600"
                          />
                        )}
                        <Icon
                          size={16}
                          className={
                            isActive
                              ? "text-brand-600"
                              : "text-slate-500 group-hover:text-slate-700"
                          }
                        />
                        {item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* Sidebar footer — plan + quota preview */}
        {account && (
          <div className="relative p-3 border-t border-mailtera-border/70 backdrop-blur-sm">
            <div className="rounded-xl bg-white/70 backdrop-blur-sm border border-mailtera-border shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                  Plan
                </div>
                <span className="text-[10px] font-mono uppercase text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded">
                  {account.planTier}
                </span>
              </div>
              <div className="text-xs text-slate-600 mt-1.5">
                Quota:{" "}
                <span className="font-semibold text-slate-900 tabular-nums">
                  {account.monthlyQuota.toLocaleString()}
                </span>
                <span className="text-slate-400"> /bulan</span>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header dengan account identity + status + logout */}
        <header className="relative h-16 px-8 flex items-center justify-between overflow-hidden bg-white/70 backdrop-blur-xl">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 480px 200px at 100% 50%, rgba(68, 114, 196, 0.10) 0%, transparent 70%),
                radial-gradient(ellipse 280px 140px at 60% 0%, rgba(26, 58, 110, 0.06) 0%, transparent 70%),
                linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(247,248,251,0.1) 100%)
              `,
            }}
          />
          <svg
            aria-hidden
            className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.04]"
          >
            <defs>
              <pattern
                id="hdr-dots"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="2" cy="2" r="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="url(#hdr-dots)"
              className="text-brand-600"
            />
          </svg>

          {/* Left: Account identity */}
          <div className="relative flex items-center gap-3">
            <div className="relative">
              <div
                className="w-9 h-9 rounded-xl text-white inline-flex items-center justify-center text-[11px] font-bold shadow-sm"
                style={{
                  background:
                    "linear-gradient(135deg, #1A3A6E 0%, #4472C4 100%)",
                }}
              >
                {accountInitials || "T"}
              </div>
              {/* Status pulse dot */}
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                  account?.status === "ACTIVE"
                    ? "bg-emerald-500"
                    : account?.status === "SUSPENDED"
                      ? "bg-rose-500"
                      : "bg-amber-500"
                }`}
              >
                {account?.status === "ACTIVE" && (
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                )}
              </span>
            </div>
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-semibold">
                Account
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="font-semibold text-slate-800 text-[15px]">
                  {account?.name ?? "…"}
                </span>
                {account && (
                  <code className="font-mono text-[10px] text-slate-500 bg-white/60 border border-mailtera-border px-1.5 py-0.5 rounded backdrop-blur-sm">
                    {account.slug}
                  </code>
                )}
              </div>
            </div>
          </div>

          {/* Right: status chip + user + logout */}
          <div className="relative flex items-center gap-3">
            {account && <StatusChip status={account.status} />}
            <div className="w-px h-7 bg-gradient-to-b from-transparent via-mailtera-border to-transparent" />
            <div className="text-right leading-tight">
              <p className="text-[11px] text-slate-500 font-mono truncate max-w-[220px]">
                {me?.userId ? shortId(me.userId) : ""}
              </p>
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-semibold mt-0.5">
                Owner
              </p>
            </div>
            <button
              onClick={() => void logout()}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>

          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.8) 20%, rgba(226, 232, 240, 1) 50%, rgba(226, 232, 240, 0.8) 80%, transparent 100%)",
            }}
          />
        </header>

        {/* Non-active status banner */}
        {account && account.status !== "ACTIVE" && (
          <StatusBanner status={account.status} />
        )}

        <main className="flex-1 px-8 py-8 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const cfg =
    status === "ACTIVE"
      ? {
          label: "Aktif",
          tone: "text-emerald-700 bg-emerald-50/70 border-emerald-200/70",
          dot: "bg-emerald-500",
        }
      : status === "SUSPENDED"
        ? {
            label: "Suspended",
            tone: "text-rose-700 bg-rose-50/70 border-rose-200/70",
            dot: "bg-rose-500",
          }
        : {
            label: "Pending",
            tone: "text-amber-700 bg-amber-50/70 border-amber-200/70",
            dot: "bg-amber-500",
          };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-sm ${cfg.tone}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden />
      {cfg.label}
    </span>
  );
}

function StatusBanner({ status }: { status: "SUSPENDED" | "PENDING" }) {
  const cfg =
    status === "SUSPENDED"
      ? {
          tone: "bg-rose-50 border-rose-200 text-rose-900",
          title: "Akun di-suspend",
          message:
            "Layanan dihentikan sementara karena deteksi bounce/complaint rate tinggi. Hubungi tim Mailtera untuk review + reactivate.",
        }
      : {
          tone: "bg-amber-50 border-amber-200 text-amber-900",
          title: "Akun belum aktif",
          message:
            "Menunggu aktivasi. Sementara masih bisa explore dashboard, tapi sending akan tertahan.",
        };
  return (
    <div className={`border-b ${cfg.tone}`}>
      <div className="px-8 py-3 flex items-start gap-3 max-w-6xl mx-auto w-full text-sm">
        <span className="font-semibold">{cfg.title}.</span>
        <span>{cfg.message}</span>
      </div>
    </div>
  );
}

function shortId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}
